/**
 * @author EdenCha <eden@nurigo.net>
 */
import React, { Component } from 'react'
import { connect } from 'react-redux'
import autoBind from 'auto-bind'
import { withRouter } from 'react-router-dom'
import uniq from 'lodash/uniq'
import jwt from 'jsonwebtoken'
import cookies from 'js-cookie'
import urlParse from 'url-parse'
import Jimp from 'jimp/es'

// lib
import bindStateToProps from 'lib/bindStateToProps'
import bindActionCreators from 'lib/bindActionCreators'
import isEqualProps from 'lib/isEqualProps'
import mapActionLog from 'lib/mapActionLog'
import proxyOverride from 'lib/proxyOverride'
// import convertImgUrlToBase64 from 'lib/convertImgUrlToBase64'
import { PngIcoConverter } from 'lib/pngToIco'
import detectCookieChanged from 'lib/detectCookieChanged'
import { isObject, isEmptyString } from 'lib/detectType'

// redux modules (action)
// import { actions as formActions } from 'store/modules/form'

import config from 'config'
import { actions as usersActions } from 'store/modules/users'
import { actions as snackbarActions } from 'store/modules/snackbar'

// react components
import MySiteLayout from 'components/organisms/MySiteLayout'

class MySiteLayoutContainer extends Component {
  constructor(props) {
    super(props)
    autoBind(this)
    mapActionLog(this)
    proxyOverride(this)
    this.state = {}
    detectCookieChanged(this.onChangeCookie, this.intervalRef)
  }

  componentDidMount = () => {
    this.initMysiteLayout()
    this.getFavoriteMenu()
  }

  componentDidUpdate = prevProps => {
    if (!isEqualProps(this.props, prevProps, ['preferences'])) {
      this.getFavoriteMenu()
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqualProps(nextProps, this.props) ||
      !isEqualProps(nextState, this.state)
    )
  }

  // ahid 재설정 시 실행됨
  initMysiteLayout = () => {
    this.getAhid()
    this.changeFavicon(this.ahid?.logoUrl)
  }

  getAhid = () => {
    if (config.isCoolsms) return false
    if (this.ahid !== undefined) return this.ahid
    this.ahid = jwt.decode(cookies.get('AHID'))
    if (isObject(this.ahid)) {
      const logoUrl = this.ahid?.logoUrl
      const urlData = urlParse(logoUrl)
      const pathname = urlData?.pathname
      const [, appId] = pathname.match(/\/(.+?)\//) || []
      Object.assign(this.ahid, { appId })
    }
    return this.ahid
  }

  changeFaviconIco = async imageUrl => {
    if (isEmptyString(imageUrl)) {
      return
    }
    try {
      const response = await fetch(imageUrl, {
        cache: 'force-cache' // 다운로드한 이미지 캐싱
      })
      const blob = await response.blob()
      const converter = new PngIcoConverter()
      const resultBlob = await converter.convertToBlobAsync([{ png: blob }]) // Default mime type is image/x-icon
      const reader = new FileReader()
      reader.readAsDataURL(resultBlob)
      reader.onloadend = () => {
        const base64data = reader.result
        // base64 converted!
        this.changeFaviconHtml(base64data)
      }
    } catch (error) {}
  }

  changeFavicon = async imageUrl => {
    if (isEmptyString(imageUrl)) {
      return
    }
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.onloadend = async () => {
        const base64data = reader.result
        const mini = await this.imageToPng(base64data)
        this.changeFaviconHtml(mini)
      }
    } catch (error) {}
  }

  createBase64ToBlobObj = binaryString => {
    const blob = new Blob([binaryString], { type: 'image/png' })
    return blob
  }

  imageToPng = fileData => {
    if (isEmptyString(fileData)) {
      return
    }
    return new Promise((resolve, reject) => {
      Jimp.read(Buffer.from(fileData.split(`;base64,`)?.[1], 'base64'))
        .then(async image => {
          image.cover(
            64,
            64,
            Jimp.HORIZONTAL_ALIGN_CENTER,
            Jimp.VERTICAL_ALIGN_MIDDLE
          )
          image.quality(30) // save
          const mime = image.getMIME()
          // const extension = image.getExtension()
          const mainThumbnailfileData = await image.getBase64Async(mime)
          // mime 제거
          // mainThumbnailfileData.split(`;base64,`)?.[1]
          resolve(mainThumbnailfileData)
        })
        .catch(reject)
    })
  }

  changeFaviconHtml = str => {
    var link = document.querySelector("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      link.sizes = '64x64'
      document.head.appendChild(link)
    }
    link.href = str
  }

  getFavoriteMenu = () => {
    const { preferences } = this.props
    const favoriteMenu = preferences?.value?.favoriteMenu || []
    this.setState({ favoriteMenu })
  }

  setFavoriteMenu = link => {
    const { UsersActions, preferences } = this.props
    const favoriteMenu = [].concat(preferences?.value?.favoriteMenu || [])
    const index = favoriteMenu.indexOf(link)
    if (index === -1) {
      favoriteMenu.push(link)
    } else {
      delete favoriteMenu[index]
    }
    return UsersActions.updateMyPreferences({
      ...(preferences?.value || {}),
      favoriteMenu: uniq(favoriteMenu).filter(d => d)
    })
  }

  onClickFavoriteIcon = async (event, isFavorite, data) => {
    event.preventDefault()
    event.stopPropagation()
    const { SnackbarActions } = this.props
    const { name, link } = data
    await this.setFavoriteMenu(link)
    SnackbarActions.new({
      severity: 'info',
      message: isFavorite
        ? `${name} 메뉴 즐겨찾기를 해제했습니다.`
        : `${name} 메뉴 즐겨찾기를 설정했습니다.`
    })
  }

  getMysiteDrawerMenu = () => {
    const { auth = {}, menus = [] } = this.props
    // 개설자 아닌 마이사이트 내 회원 중 관리자가 지정된 경우
    const appId = auth?.appId
    const isMySiteAdmin = auth?.isMysiteAdmin === true
    if (!isMySiteAdmin) return menus
    const menuLength = menus.length
    const mysiteManagementMenuData = {
      headerTitle: '마이사이트 관리',
      list: [
        {
          name: '가입된 계정목록',
          icon: 'supervisor_account',
          link: `/mysites/${appId}/members`
        },
        {
          name: '계정별 수익금 설정',
          icon: 'attach_money',
          link: `/mysites/${appId}/profit`
        }
      ]
    }
    const front = menus.slice(0, menuLength - 1)
    const end = menus.slice(-1)
    return front.concat(mysiteManagementMenuData, ...end)
  }

  onChangeCookie = async data => {
    if (
      this.updateTokenProcessing === true ||
      !isObject(data?.diff) ||
      Object.keys(data?.diff).indexOf('AHID') === -1
    ) {
      return
    }
    try {
      this.updateTokenProcessing = true
      this.initMysiteLayout()
    } catch (error) {
      this.onLogout()
    } finally {
      this.updateTokenProcessing = false
    }
  }

  render() {
    return (
      <MySiteLayout
        {...this.props}
        {...this.state}
        proxy={this}
        menus={this.getMysiteDrawerMenu()}
      />
    )
  }
}

export default withRouter(
  connect(
    bindStateToProps({
      consoleLayout: ['menus', 'routeParams'],
      users: ['preferences']
    }),
    bindActionCreators({
      usersActions,
      snackbarActions
    })
  )(MySiteLayoutContainer)
)
